import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import ProductCard from "../components/ProductCard";
import { CaretDown } from "@phosphor-icons/react";

const BASE_CATEGORIES = ["bras", "panties", "sets", "sleepwear", "nightwear", "bodysuits"];
const HIDDEN_CATEGORIES = ["roleplay"];
const humanize = (s) => s.split("-").map(w => w[0]?.toUpperCase() + w.slice(1)).join(" ");

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const category = params.get("category") || "all";
  const [products, setProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [sort, setSort] = useState("featured");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch all products once for the dynamic category list
    api.get("/products").then(r => setAllProducts(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    api.get("/products", { params: category !== "all" ? { category } : {} })
      .then(r => setProducts(r.data))
      .finally(() => setLoading(false));
  }, [category]);

  const dynamicCats = Array.from(new Set([...BASE_CATEGORIES, ...allProducts.map(p => p.category).filter(Boolean)])).filter(c => !HIDDEN_CATEGORIES.includes(c));
  const CATEGORIES = [{ label: "All", value: "all" }, ...dynamicCats.map(c => ({ label: humanize(c), value: c }))];

  const sorted = [...products].sort((a, b) => {
    if (sort === "price-asc") return a.price - b.price;
    if (sort === "price-desc") return b.price - a.price;
    if (sort === "name") return a.name.localeCompare(b.name);
    return (b.featured ? 1 : 0) - (a.featured ? 1 : 0);
  });

  const setCategory = (c) => {
    if (c === "all") setParams({});
    else setParams({ category: c });
  };

  return (
    <div className="max-w-[1600px] mx-auto px-6 lg:px-12 py-16 lg:py-24" data-testid="shop-page">
      <div className="mb-16">
        <div className="overline text-[#4A1525] mb-4">Collection</div>
        <h1 className="font-serif text-5xl md:text-7xl tracking-tighter">
          {category === "all" ? "The Edit" : humanize(category)}
        </h1>
        <p className="text-[#5C5C5C] mt-4 max-w-xl">Every piece is crafted in limited runs, from ethically sourced materials.</p>
      </div>

      {/* Filters */}
      <div className="flex justify-between items-center flex-wrap gap-6 mb-12 border-y border-[#E6DED5] py-5">
        <div className="flex gap-2 flex-wrap">
          {CATEGORIES.map(c => (
            <button key={c.value} onClick={() => setCategory(c.value)} className={`overline px-4 py-2 transition-colors ${category === c.value ? "bg-[#1A1A1A] text-[#FAF8F5]" : "text-[#1A1A1A] hover:bg-[#F0EBE1]"}`} data-testid={`cat-filter-${c.value}`}>
              {c.label}
            </button>
          ))}
        </div>
        <div className="relative">
          <select value={sort} onChange={(e) => setSort(e.target.value)} className="appearance-none bg-transparent overline pr-8 pl-4 py-2 border border-[#1A1A1A]" data-testid="sort-select">
            <option value="featured">Featured</option>
            <option value="price-asc">Price: Low to high</option>
            <option value="price-desc">Price: High to low</option>
            <option value="name">Name</option>
          </select>
          <CaretDown size={12} weight="light" className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12">
          {Array(8).fill(0).map((_, i) => <div key={i} className="aspect-[3/4] bg-[#F0EBE1] animate-pulse" />)}
        </div>
      ) : sorted.length === 0 ? (
        <div className="text-center py-24 text-[#5C5C5C]" data-testid="no-products">No pieces in this category yet.</div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12" data-testid="products-grid">
          {sorted.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      )}
    </div>
  );
}
